/**
 * opencode headless event mapping — `opencode run --format json` NDJSON lines
 * into HarnessEvents.
 *
 * Pure and side-effect free so the whole mapping is unit-testable without
 * spawning anything. The shapes below were captured from opencode 1.18.15:
 *
 *   {"type":"step_start","timestamp":…,"sessionID":"ses_…","part":{…,"type":"step-start"}}
 *   {"type":"text","…","part":{"type":"text","text":"HEADLESS","time":{…}}}
 *   {"type":"tool_use","…","part":{"type":"tool","tool":"write","state":{"status":"completed","output":"…"}}}
 *   {"type":"step_finish","…","part":{"type":"step-finish", …usage…}}
 *
 * A run emits one or more step_start/…/step_finish groups and then exits; the
 * process exit is the end of the turn, so `result` is synthesized by the caller
 * rather than appearing in the stream.
 */

import type { HarnessEvent } from "../types.js";

/** One parsed NDJSON line. Deliberately loose — opencode may add fields. */
export interface OpenCodeEvent {
  type?: string;
  timestamp?: number;
  sessionID?: string;
  /** Present on `type: "error"` — see errorMessageOf. */
  error?: unknown;
  part?: {
    type?: string;
    text?: string;
    tool?: string;
    state?: { status?: string; output?: unknown; input?: unknown; error?: unknown };
    tokens?: unknown;
    cost?: unknown;
    [k: string]: unknown;
  };
  [k: string]: unknown;
}

/** Token/cost totals accumulated across a run's step_finish parts. */
export interface OpenCodeUsage {
  inputTokens: number;
  outputTokens: number;
  totalCostUsd: number;
}

/**
 * Parse one NDJSON line. Returns null for blank lines and for anything that is
 * not JSON — `--format json` writes events to stdout, but a stray banner or
 * warning must never abort the turn.
 */
export function parseOpenCodeLine(line: string): OpenCodeEvent | null {
  const trimmed = line.trim();
  if (!trimmed.startsWith("{")) return null;
  try {
    return JSON.parse(trimmed) as OpenCodeEvent;
  } catch {
    return null;
  }
}

function numberAt(source: unknown, ...keys: string[]): number {
  let cursor: unknown = source;
  for (const key of keys) {
    if (typeof cursor !== "object" || cursor === null) return 0;
    cursor = (cursor as Record<string, unknown>)[key];
  }
  return typeof cursor === "number" && Number.isFinite(cursor) ? cursor : 0;
}

/**
 * Fold a step_finish part's usage into a running total. opencode nests token
 * counts under `tokens` and cost under `cost`; both are treated as optional
 * because a provider that reports neither must not zero the accumulator.
 */
export function accumulateUsage(event: OpenCodeEvent, into: OpenCodeUsage): void {
  const part = event.part;
  if (!part) return;
  into.inputTokens += numberAt(part.tokens, "input");
  into.outputTokens += numberAt(part.tokens, "output");
  const cost = part.cost;
  if (typeof cost === "number" && Number.isFinite(cost)) into.totalCostUsd += cost;
}

/**
 * Map one opencode event to a HarnessEvent, or null when it carries no
 * information the runner consumes (step boundaries, unknown future types).
 *
 * Tool names arrive namespaced by their MCP server — a Conveyor tool registered
 * as `post_to_chat` is reported by opencode as `conveyor_post_to_chat`. The name
 * is passed through verbatim: the runner's activity summaries are display-only,
 * and rewriting it would misreport what actually ran.
 */
export function mapOpenCodeEvent(event: OpenCodeEvent): HarnessEvent | null {
  const part = event.part;
  if (!part) return null;
  if (part.type === "text") return mapTextPart(part);
  if (part.type === "tool") return mapToolPart(part);
  return null;
}

type OpenCodePart = NonNullable<OpenCodeEvent["part"]>;

function mapTextPart(part: OpenCodePart): HarnessEvent | null {
  const text = typeof part.text === "string" ? part.text : "";
  // opencode emits whitespace-only text parts between tool calls; they would
  // otherwise become empty assistant messages in card chat.
  if (text.trim() === "") return null;
  return {
    type: "assistant",
    message: { role: "assistant", content: [{ type: "text", text }] },
  };
}

function mapToolPart(part: OpenCodePart): HarnessEvent | null {
  const status = part.state?.status;
  // Only terminal states are reported: opencode re-emits a part as it moves
  // pending → running → completed, and the runner's activity batching would
  // otherwise count one call several times.
  if (status !== "completed" && status !== "error") return null;
  // A tool call reaches the runner the same way the Claude transcript delivers
  // one — an assistant message carrying a `tool_use` content block, which
  // event-handlers turns into a `tool_use` event and an activity summary.
  return {
    type: "assistant",
    message: {
      role: "assistant",
      content: [
        {
          type: "tool_use",
          name: typeof part.tool === "string" ? part.tool : "unknown",
          input: part.state?.input ?? {},
          ...(typeof part.id === "string" ? { id: part.id } : {}),
        },
      ],
    },
  };
}

/** Tool output is free-form; keep text as-is and JSON-encode anything else. */
export function stringifyToolOutput(output: unknown): string {
  if (output === undefined || output === null) return "";
  if (typeof output === "string") return output;
  try {
    return JSON.stringify(output);
  } catch {
    return String(output);
  }
}

/**
 * A provider/runtime failure opencode reported as a first-class event, or null.
 *
 * A credential failure is the common case and it arrives HERE, on stdout, not on
 * stderr — which is empty. Without reading this event an auth failure surfaces as
 * a bare "exited with code 1" and a card gives no clue why (observed live: an
 * invalid OpenAI key produced exactly that). opencode redacts the credential in
 * its own message before we ever see it.
 */
export function errorMessageOf(event: OpenCodeEvent): string | null {
  if (event.type !== "error") return null;
  const error = event.error as
    | { name?: unknown; data?: { message?: unknown; statusCode?: unknown } }
    | undefined;
  const message = typeof error?.data?.message === "string" ? error.data.message : null;
  const name = typeof error?.name === "string" ? error.name : "error";
  const status =
    typeof error?.data?.statusCode === "number" ? ` (HTTP ${error.data.statusCode})` : "";
  return message ? `${name}${status}: ${message}` : `${name}${status}`;
}

/** The session id opencode assigned, for `--session` resume on the next turn. */
export function sessionIdOf(event: OpenCodeEvent): string | null {
  return typeof event.sessionID === "string" && event.sessionID.length > 0 ? event.sessionID : null;
}

// ── Plugin bus events ────────────────────────────────────────────────────
//
// The Conveyor opencode plugin (see ./plugin.ts) appends the CLIENT engine's
// bus events to an NDJSON sink the PTY session tails. Bus events wrap their
// payload in `properties` (the server's SSE stream uses `data` — a different
// surface; do not conflate them). `message.part.updated` carries the SAME
// `part` shapes as the NDJSON stream above, so the mapping and usage helpers
// are shared: `busPartOf` unwraps the part and callers feed `{ part }` back
// into `mapOpenCodeEvent` / `accumulateUsage`.
//
// Parts do NOT carry the author's role. `message.updated` (which the bus emits
// BEFORE a message's parts — verified in the committed fixtures) does, via
// `properties.info.{id,role}` — consumers map roles first and only surface
// parts of assistant messages, or the user's own pasted prompt would echo back
// into card chat as an assistant message.

/** One parsed plugin-bus NDJSON line. Loose — opencode may add fields. */
export interface OpenCodeBusEvent {
  type?: string;
  properties?: Record<string, unknown> & {
    sessionID?: string;
    status?: { type?: string };
    info?: { id?: string; role?: string; sessionID?: string };
    part?: OpenCodeEvent["part"] & { messageID?: string };
  };
}

/** Parse one sink line. Null for blanks and anything that is not JSON. */
export function parseBusLine(line: string): OpenCodeBusEvent | null {
  const trimmed = line.trim();
  if (!trimmed.startsWith("{")) return null;
  try {
    return JSON.parse(trimmed) as OpenCodeBusEvent;
  } catch {
    return null;
  }
}

/** The opencode session id the event belongs to, from whichever field has it. */
export function busSessionIdOf(event: OpenCodeBusEvent): string | null {
  const direct = event.properties?.sessionID;
  if (typeof direct === "string" && direct.length > 0) return direct;
  const viaInfo = event.properties?.info?.sessionID;
  return typeof viaInfo === "string" && viaInfo.length > 0 ? viaInfo : null;
}

/**
 * The turn-end signal. 1.18.4 emits BOTH `session.idle` and
 * `session.status {type:"idle"}` back-to-back; 1.18.15 emits only the latter —
 * consumers must accept either, and end a turn at most once.
 */
export function isIdleEvent(event: OpenCodeBusEvent): boolean {
  if (event.type === "session.idle") return true;
  return event.type === "session.status" && event.properties?.status?.type === "idle";
}

/** The turn-started signal (`session.status {type:"busy"}`). */
export function isBusyEvent(event: OpenCodeBusEvent): boolean {
  return event.type === "session.status" && event.properties?.status?.type === "busy";
}

/** The message info from a `message.updated` event, or null. */
export function busInfoOf(event: OpenCodeBusEvent): { id?: string; role?: string } | null {
  if (event.type !== "message.updated") return null;
  return event.properties?.info ?? null;
}

/** The part from a `message.part.updated` event, or null. */
export function busPartOf(
  event: OpenCodeBusEvent,
): NonNullable<OpenCodeBusEvent["properties"]>["part"] | null {
  if (event.type !== "message.part.updated") return null;
  return event.properties?.part ?? null;
}

/**
 * Build the terminal `result` for a finished run. opencode's stream has no
 * result record — the process exiting IS the end of the turn — so exit code and
 * the accumulated usage are all we have.
 */
export function buildResultEvent(
  exitCode: number,
  usage: OpenCodeUsage,
  assistantText: string,
  stderrTail: string,
  reportedError?: string | null,
): HarnessEvent {
  if (exitCode === 0) {
    return {
      type: "result",
      subtype: "success",
      // The runner substitutes "Task completed." for an empty summary, so a run
      // that only used tools still reads sensibly in chat.
      result: assistantText,
      total_cost_usd: usage.totalCostUsd,
    };
  }
  // A reported error event is far more useful than the exit code, so it leads.
  const errors = reportedError ? [reportedError] : [`opencode run exited with code ${exitCode}`];
  if (stderrTail.trim()) errors.push(`stderr:\n${stderrTail.trim()}`);
  return { type: "result", subtype: "error", errors };
}
